<template>
  <div
    ref="wrap"
    :class="{ 'fills-viewport': isFillViewport }">
    <UiDynamicTableBase
      v-bind="{ ...baseProps, ...$attrs }">
      <template
        v-for="(_, name) in $slots"
        :key="name"
        #[name]="slotData">
        <slot
          :name="name"
          v-bind="slotData ?? {}" />
      </template>
    </UiDynamicTableBase>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { IHeaderRow, IBodyRow } from '@team-milva/lab-components'

export default {
  name: 'UiDynamicTable',
  inheritAttrs: false,
  props: {
    title: { type: String, default: undefined },
    headerRow: {
      type: Object as PropType<IHeaderRow>,
      required: true as const,
    },
    bodyRows: { type: Array as PropType<IBodyRow[]>, required: true as const },
    loadingText: { type: String, default: undefined },
    isEditableTitle: { type: Boolean, default: undefined },
    titlePlaceholder: { type: String, default: undefined },
    hasSearch: { type: Boolean, default: undefined },
    hasCSVExport: { type: Boolean, default: undefined },
    csvExportLabel: { type: String, default: undefined },
    searchPlaceholder: { type: String, default: undefined },
    isFillViewport: { type: Boolean, default: false },
  },

  computed: {
    baseProps() {
      const { isFillViewport: _, ...rest } = this.$props
      return rest
    },
  },

  watch: {
    bodyRows() {
      if (this.isFillViewport) {
        this.$nextTick(() => this.updateHeight())
      }
    },
  },

  methods: {
    isInFlow(el: HTMLElement): boolean {
      const { position, display } = getComputedStyle(el)
      return position !== 'fixed' && position !== 'absolute' && display !== 'none'
    },

    updateHeight() {
      const wrap = this.$refs.wrap as HTMLElement
      const scrollWrap = wrap.querySelector<HTMLElement>('.table-scroll-wrap')
      if (!scrollWrap) return

      // Find the first scrollable ancestor — the element whose overflow we must prevent
      let scrollContainer: HTMLElement | null = wrap.parentElement
      while (scrollContainer && scrollContainer !== document.documentElement) {
        const oy = getComputedStyle(scrollContainer).overflowY
        if (oy === 'auto' || oy === 'scroll') break
        scrollContainer = scrollContainer.parentElement
      }
      if (!scrollContainer || scrollContainer === document.documentElement) return

      // Scroll-corrected distance from the scroll container's top to the scroll wrap's top.
      // Adding scrollTop corrects for any existing scroll so the result is stable.
      const containerRect = scrollContainer.getBoundingClientRect()
      const scrollWrapPosInContainer =
        scrollWrap.getBoundingClientRect().top - containerRect.top + scrollContainer.scrollTop

      // Space consumed below our wrapper: sibling heights + ancestor padding-bottoms,
      // walking from wrap up to (but not including) the scroll container.
      //
      // Only in-flow siblings take space. A side pane (`UiSidePane` is
      // `position: fixed`) rendered as the table's next sibling — the pattern in
      // the full-screen setup views — is 700px+ tall but consumes nothing in the
      // scroll container, and counting it collapsed the table to 0px whenever
      // the height was recomputed with the pane open.
      let bottomSpace = 0
      let el: HTMLElement | null = wrap
      while (el && el !== scrollContainer) {
        let sib = el.nextElementSibling as HTMLElement | null
        while (sib) {
          if (this.isInFlow(sib)) bottomSpace += sib.getBoundingClientRect().height
          sib = sib.nextElementSibling as HTMLElement | null
        }
        const parent = el.parentElement as HTMLElement | null
        if (parent && parent !== scrollContainer) {
          bottomSpace += parseFloat(getComputedStyle(parent).paddingBottom) || 0
        }
        el = parent
      }
      bottomSpace += parseFloat(getComputedStyle(scrollContainer).paddingBottom) || 0

      // By construction: scrollWrapPosInContainer + maxHeight + bottomSpace = containerClientHeight,
      // so the scroll container's content fits exactly with zero overflow.
      const maxHeight = Math.max(0, scrollContainer.clientHeight - scrollWrapPosInContainer - bottomSpace)
      wrap.style.setProperty('--table-max-height', `${maxHeight}px`)
    },
  },

  mounted() {
    if (this.isFillViewport) {
      this.$nextTick(() => {
        this.updateHeight()
        window.addEventListener('resize', this.updateHeight)
      })
    }
  },

  beforeUnmount() {
    if (this.isFillViewport) {
      window.removeEventListener('resize', this.updateHeight)
    }
  },
}
</script>

<style>
@reference "../../globals/css/base.css";

.s-dynamic-table {
  /* No card — table sits directly on the page background */
  padding: 0 !important;

  .top .title {
    @apply text-xl font-semibold;
  }

  .table {
    @apply text-sm;
    width: 100%;

    th {
      @apply text-text-muted font-normal;
      padding: 8px 24px;
      font-size: 12px;
      white-space: nowrap;
      border-bottom: 1px solid var(--color-border-subtle);
    }

    tbody tr {
      &.is-clickable {
        cursor: pointer;
        /* Light gray hover on white background */
        @apply hover:bg-surface-muted;
      }

      td {
        height: 48px;
        padding: 0 24px;
        vertical-align: middle;
        @apply border-b border-border-subtle font-medium;
      }

      .item {
        .is-faded {
          @apply text-text-secondary;
        }
      }

      .loading-text {
        @apply text-sm text-text-secondary;
      }

      .row-actions,
      .first-col-actions {
        .loading-text {
          @apply text-sm text-text-secondary;
        }

        .row-action {
          @apply cursor-pointer hover:underline;

          .IconWrap {
            @apply rounded-md text-text-primary hover:bg-surface-sunken hover:text-text-primary;
          }
        }
      }
    }

    th.action-col {
      @apply bg-surface;
    }

    td.action-col {
      @apply bg-surface;
    }

    tbody tr.is-clickable:hover td.action-col {
      @apply bg-surface-muted;
    }

    th.first-col {
      background-color: var(--table-sticky-bg, var(--color-surface));
    }

    td.first-col {
      background-color: var(--table-sticky-bg, var(--color-surface));
    }

    tbody tr.is-clickable:hover td.first-col {
      @apply bg-surface-muted;
    }

  }
}

.fills-viewport {
  .table-scroll-wrap {
    max-height: var(--table-max-height, none);
    overflow-y: auto;
  }
}
</style>
